import React from 'react';
import { Database, Lock, Mail, ShieldCheck, Users } from 'lucide-react';
import StatCard from '../StatCard';
import { dashboardStatAccents, type DashboardAccent } from '../../themeHelpers';
import { useSystemHealthSummary } from '../../../system-health/useSystemHealthSummary';
import { formatFileSize } from '../../../../components/attachments/fileIconUtils';
import { healthLabel } from '../../../system-health/utils';

export type SystemHealthMetricKey =
  | 'overallStatus'
  | 'diskFree'
  | 'emailSyncHealth'
  | 'failedLogins'
  | 'onlineUsers';

const icons: Record<SystemHealthMetricKey, React.ReactNode> = {
  overallStatus: <ShieldCheck aria-hidden />,
  diskFree: <Database aria-hidden />,
  emailSyncHealth: <Mail aria-hidden />,
  failedLogins: <Lock aria-hidden />,
  onlineUsers: <Users aria-hidden />,
};

const accents: Record<SystemHealthMetricKey, DashboardAccent> = {
  overallStatus: dashboardStatAccents.systemOk,
  diskFree: dashboardStatAccents.disk,
  emailSyncHealth: dashboardStatAccents.email,
  failedLogins: dashboardStatAccents.security,
  onlineUsers: dashboardStatAccents.users,
};

const titles: Record<SystemHealthMetricKey, string> = {
  overallStatus: 'System status',
  diskFree: 'Disk free',
  emailSyncHealth: 'Email sync',
  failedLogins: 'Failed logins (24h)',
  onlineUsers: 'Online users',
};

export interface SystemHealthStatWidgetProps {
  metricKey?: SystemHealthMetricKey;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const SystemHealthStatWidget: React.FC<SystemHealthStatWidgetProps> = ({
  metricKey: metricKeyProp,
  settings,
  embedded,
}) => {
  const metricKey = (settings?.metricKey ?? metricKeyProp ?? 'overallStatus') as SystemHealthMetricKey;
  const { data, isLoading, error } = useSystemHealthSummary();

  if (error) {
    return (
      <StatCard
        title={titles[metricKey]}
        value="—"
        change="Unable to load health data"
        changeVariant="warning"
        icon={icons[metricKey]}
        accent={accents[metricKey]}
        embedded={embedded}
      />
    );
  }

  let value: string | number = '—';
  let change: string | undefined;
  let changeVariant: 'success' | 'warning' | 'neutral' = 'neutral';

  if (data) {
    switch (metricKey) {
      case 'overallStatus':
        value = healthLabel(data.overallStatus);
        if (data.warningCount > 0) {
          change = `${data.warningCount} active warning${data.warningCount !== 1 ? 's' : ''}`;
          changeVariant = data.overallStatus === 'error' ? 'warning' : 'neutral';
        } else {
          change = 'All checks passing';
          changeVariant = 'success';
        }
        break;
      case 'diskFree':
        value = formatFileSize(data.disk.availableBytes);
        change = `${data.disk.usedPercent}% used on /`;
        changeVariant = data.disk.usedPercent >= 90 ? 'warning' : data.disk.usedPercent >= 80 ? 'warning' : 'neutral';
        break;
      case 'emailSyncHealth': {
        const { healthy, warning, error: errCount, total } = data.emailSync;
        value = total === 0 ? 'No accounts' : `${healthy}/${total} healthy`;
        if (errCount > 0) {
          change = `${errCount} error${errCount !== 1 ? 's' : ''}${warning > 0 ? `, ${warning} warning${warning !== 1 ? 's' : ''}` : ''}`;
          changeVariant = 'warning';
        } else if (warning > 0) {
          change = `${warning} account${warning !== 1 ? 's' : ''} need attention`;
          changeVariant = 'warning';
        } else if (total > 0) {
          change = 'All accounts syncing normally';
          changeVariant = 'success';
        }
        break;
      }
      case 'failedLogins':
        value = data.security.failedLogins24h;
        change = value === 0 ? 'No failed attempts' : 'Last 24 hours';
        changeVariant = (value as number) >= 10 ? 'warning' : value === 0 ? 'success' : 'neutral';
        break;
      case 'onlineUsers':
        value = data.onlineUsers.uniqueUsers;
        change =
          data.onlineUsers.totalSockets > data.onlineUsers.uniqueUsers
            ? `${data.onlineUsers.totalSockets} active sessions`
            : data.onlineUsers.uniqueUsers === 0
              ? 'Nobody connected'
              : 'Currently connected';
        changeVariant = 'neutral';
        break;
    }
  }

  return (
    <StatCard
      title={titles[metricKey]}
      value={value}
      change={change}
      changeVariant={changeVariant}
      icon={icons[metricKey]}
      accent={accents[metricKey]}
      isLoading={isLoading}
      embedded={embedded}
    />
  );
};

export default SystemHealthStatWidget;
